import { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Download,
  ExternalLink,
  FileText,
  FolderOpen,
  Globe,
  Presentation,
  Sparkles,
} from "lucide-react";
import {
  driveDownloadUrl,
  driveFileUrl,
  driveFolderUrl,
} from "../resourcesData";
import LearnMore from "./LearnMore";
import VideoCard from "./VideoCard";

const CARD_CLASS =
  "group relative flex h-full flex-col gap-2 rounded-lg border border-[#DDDDDD] bg-white p-4 transition hover:shadow-md";
const BADGE_CLASS =
  "flex w-fit items-center gap-1 rounded-full bg-[#EAF4FF] px-2 py-0.5 text-xs font-semibold uppercase tracking-wide text-[#3387CF]";
const CTA_CLASS =
  "flex shrink-0 items-center gap-1 whitespace-nowrap text-xs font-semibold text-[#3387CF] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#3387CF] focus-visible:ring-offset-2 group-hover:underline";
// Covers the card so the whole surface is one clickable target.
const STRETCHED_CLASS = "after:absolute after:inset-0 after:content-['']";
// `z-[1]` to sit above the stretched ::after, which would otherwise swallow
// this link's clicks.
const SECONDARY_CLASS =
  "relative z-[1] flex w-fit items-center gap-1 text-xs text-[#3387CF] hover:underline sm:text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#3387CF] focus-visible:ring-offset-2";

/**
 * `pointer-events-none` is what makes the preview clickable: the hover fade
 * gives the image opacity < 1, which paints it above the link's stretched
 * ::after and would otherwise swallow the click. Drop it and previews go dead.
 *
 * Previews are letterboxed, not cropped - they range from a tall poster to a
 * 16:9 slide. A failed image drops the frame rather than showing a broken box.
 */
function CardPreview({ src, alt }) {
  const [failed, setFailed] = useState(false);

  if (failed) {
    return null;
  }

  return (
    <div className="pointer-events-none flex h-40 w-full items-center justify-center overflow-hidden rounded border border-[#EEEEEE] bg-[#F4F4F3] sm:h-48">
      {/* Explicit sizing: src/index.css sets `img { max-width: unset }` */}
      <img
        src={src}
        alt={alt || ""}
        loading="lazy"
        referrerPolicy="no-referrer"
        onError={() => setFailed(true)}
        className="h-full w-auto max-w-full object-contain transition group-hover:opacity-90"
      />
    </div>
  );
}

function getPresentation(item) {
  switch (item.kind) {
    case "driveFolder":
      return {
        Icon: FolderOpen,
        badge: "Google Drive folder",
        href: driveFolderUrl(item.driveId),
        cta: item.ctaLabel || "Open in Google Drive",
      };
    case "driveDownload":
      return {
        Icon: item.fileKind === "slides" ? Presentation : FileText,
        badge: item.fileKind === "slides" ? "Slides" : "PDF",
        href: driveDownloadUrl(item.driveId),
        cta: item.ctaLabel || "Download",
        isDownload: true,
      };
    case "driveFile":
      return {
        Icon: item.fileKind === "slides" ? Presentation : FileText,
        badge: item.fileKind === "slides" ? "Slides" : "PDF",
        href: driveFileUrl(item.driveId),
        cta: item.ctaLabel || "Open in Google Drive",
      };
    case "external":
      return {
        Icon: Globe,
        badge: "Website",
        href: item.url,
        cta: item.ctaLabel || "Visit site",
      };
    case "route":
      return { Icon: Sparkles, badge: "In app", cta: item.ctaLabel || "Open" };
    default:
      return null;
  }
}

export default function ResourceCard({ item }) {
  const navigate = useNavigate();

  if (item.kind === "video") {
    return <VideoCard item={item} />;
  }

  const presentation = getPresentation(item);
  if (!presentation) {
    return null;
  }

  const { Icon, badge, href, cta, isDownload } = presentation;
  const CtaIcon = isDownload ? Download : ExternalLink;

  // Downloads open a new tab too, so a blocked Drive response cannot replace
  // the app page; the tab closes itself once the file starts saving.
  const action = href ? (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className={`${CTA_CLASS} ${STRETCHED_CLASS}`}
    >
      {cta}
      <CtaIcon className="h-3.5 w-3.5" />
      <span className="sr-only">
        {isDownload ? "(downloads a file)" : "(opens in a new tab)"}
      </span>
    </a>
  ) : (
    <button
      type="button"
      onClick={() => navigate(item.to)}
      className={`${CTA_CLASS} ${STRETCHED_CLASS} text-left`}
    >
      {cta}
    </button>
  );

  return (
    <div className={CARD_CLASS}>
      <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-2">
        <span className={BADGE_CLASS}>
          <Icon className="h-3.5 w-3.5" />
          {badge}
        </span>
        {action}
      </div>

      {item.previewSrc && (
        <CardPreview src={item.previewSrc} alt={item.previewAlt} />
      )}

      <p className="m-0 text-sm font-semibold text-[#1E2D3D] sm:text-base">
        {item.title}
      </p>
      {item.description && (
        <p className="m-0 text-sm text-[#555555] sm:text-base">{item.description}</p>
      )}
      {item.learnMore && <LearnMore {...item.learnMore} />}
      {item.secondary && (
        <a
          href={item.secondary.url}
          target="_blank"
          rel="noopener noreferrer"
          className={SECONDARY_CLASS}
        >
          {item.secondary.label}
          <ExternalLink className="h-3.5 w-3.5" />
          <span className="sr-only">(opens in a new tab)</span>
        </a>
      )}
    </div>
  );
}
